import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, inject, Input } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

import { I18n } from "@intocare/interfaces";
import { clean } from "@intocare/util";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => MultiTextInputComponent),
	multi: true,
};

@Component({
	selector: "ic-multi-input",
	styleUrls: ["input.component.scss"],
	providers: [INPUT_CONTROL_ACCESSOR],
	templateUrl: "./input.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class MultiTextInputComponent implements ControlValueAccessor {
	private cdRef = inject(ChangeDetectorRef);

	@Input() label: string;
	@Input() placeholder = "";
	@Input() disabled = false;
	@Input() required = true;
	@Input() help: string;
	@Input() languages: string[] = [];
	@Input() error = false;
	@Input() stacked = false;
	@Input() missing = false;
	@Input() missingText: string;

	@Input()
	set languagesWithErrors(languages: string[]) {
		this.langErrors = languages;
		this.errors = new Set(this.langErrors);
	}

	value: any = {};
	touched = new Set<string>([]);
	errors = new Set<string>([]);

	private langErrors: string[];
	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onModelChange = (_: unknown) => {};

	writeValue(value: any) {
		this.touched = new Set([]);
		this.value = { ...value };
		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onModelChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
		this.cdRef.markForCheck();
	}

	individualOnChange(language: string, value: I18n | string) {
		this.value[language] = value;
		this.onChange(this.value);
	}

	hasValue(language: string) {
		return Boolean(this.value[language]);
	}

	onChange(value: any) {
		this.value = clean(value);
		this.onModelChange(this.cleanValue(value));
	}

	onTouched(language: string) {
		this.touched.add(language);

		if (this.touched.size === this.languages.length) {
			this.onTouch();
		}
	}

	hasError(language: string) {
		if (!this.error) {
			return false;
		}

		// In this scenario, we do not have languagesWithErrors defined because there are no symmetrical inputs in the form. In this case, all input fields should be red
		if (!this.langErrors) {
			return true;
		}

		return this.errors.has(language);
	}

	private cleanValue(value: I18n | string) {
		if (typeof value === "string") {
			return value;
		}

		const cleaned = clean(value);

		return Object.keys(cleaned).length > 0 ? cleaned : "";
	}
}
